import { useTranslation } from "react-i18next";
import DashboardStats from "../../components/Stats/DashboardStats";
import ReactECharts from "echarts-for-react";
import { Button } from "@mantine/core";
import { useNavigate } from "react-router";
import services from "../../services";
import { useQuery } from "@tanstack/react-query";
import BarLoading from "../../components/Loading/BarLoading";
import { currencyFormatter } from "../../utils/helper";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import CustomTabs from "../../components/CustomTabs";
import { useCopilotReadable } from "@copilotkit/react-core";

import { compose, withProps, withStateHandlers } from "recompose";
import {
  GoogleMap,
  Polygon,
  withGoogleMap,
  withScriptjs,
  Marker,
  InfoWindow,
} from "react-google-maps";
import SpotsCustomList from "../../components/SpotsCustomList/SpostsCustomList";
import { useStore } from "../../hooks";
const defaultLocation = { lat: 41.095442509457285, lng: 28.802870489816268 };
const mapStyles = require("../../static/mapStyles.json");

const iconReturner = (type) => {
  switch (type) {
    case "CRITICAL":
      return require("../../assets/ChevronThreeUp.svg").default;
    case "NORMAL":
      return require("../../assets/ChevronTwoUp.svg").default;
    case "FULL":
      return require("../../assets/ChevronOneUp.svg").default;
    default:
      return require("../../assets/ChevronOneUp.svg").default;
  }
};

const MapComponent = compose(
  withStateHandlers(
    () => ({
      isOpen: false,
      openId: 0,
    }),
    {
      onToggleOpen:
        ({ isOpen }) =>
        (id) => ({
          isOpen: !isOpen,
          openId: id || 0,
        }),
    }
  ),
  withProps({
    googleMapURL:
      "https://maps.googleapis.com/maps/api/js?v=3.64&libraries=geometry,drawing&key=AIzaSyCBM4uTkUhiTpZYBUFxR5NZ2ayD6S-PrmI",
    loadingElement: <div className="w-full" style={{ height: `100%` }} />,
    containerElement: <div className="w-full" style={{ height: `100%` }} />,
    mapElement: <div className="w-full" style={{ height: `100%` }} />,
  }),
  withScriptjs,
  withGoogleMap
)(({ center, zoom = 12, data, isOpen, openId, onToggleOpen }) => {
  const { t } = useTranslation();
  return (
    <GoogleMap
      defaultZoom={zoom}
      zoom={zoom}
      center={center || defaultLocation}
      defaultCenter={defaultLocation}
      defaultOptions={mapStyles}
    >
      {data?.main_sale_areas?.map((area, index) => (
        <Polygon
          key={`main-${index}`}
          options={{
            fillColor: "rgba(0,0,0,0.4)",
            editable: false,
          }}
          paths={area.main_sale_area_coordinates}
        />
      ))}

      {data?.sale_areas?.map((area, index) => (
        <Polygon
          key={`sale-area-${index}`}
          options={{
            fillColor: "rgba(217, 119, 6, 0.7)",
            editable: false,
          }}
          paths={area.sale_area_coordinates}
        />
      ))}

      {data?.sale_spots?.map((spot, index) => (
        <Marker
          key={`spot-${index}`}
          position={{
            lat: parseFloat(spot.sale_area_coordinates[0].lat),
            lng: parseFloat(spot.sale_area_coordinates[0].lng),
          }}
          icon={{
            url: iconReturner(spot.stock_status.status),
            scaledSize: new window.google.maps.Size(65, 65),
          }}
          onClick={() => {
            onToggleOpen(index);
          }}
        >
          {isOpen && openId === index && (
            <InfoWindow onCloseClick={onToggleOpen}>
              <div className="text-black">
                <div className="font-semibold">{spot.sale_spot_name}</div>
                <div className="text-sm text-gray-600">
                  {spot.sale_area_name}
                </div>
                <div className="text-sm">
                  {t("dashboard.status")}: {spot.sale_spot_status}
                </div>
                <div className="text-sm">
                  {t("dashboard.last-updated")}:{" "}
                  {dayjs(spot.sale_spot_status_updated_at).fromNow()}
                </div>
                <div className="flex flex-row gap-2 items-center">
                  <div className="text-md">
                    {t("dashboard.stock-status-percentage")}:
                  </div>
                  <div className="text-sm">{spot.stock_status.percentage}%</div>
                </div>
                <div className="flex flex-row gap-2 items-center">
                  <div className="text-md">
                    {t("dashboard.stock-status-total-current-stock")}:
                  </div>
                  <div className="text-sm">
                    {spot.stock_status.total_current_stock} /{" "}
                    {spot.stock_status.total_capacity}
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}
    </GoogleMap>
  );
});
const Home = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState([]);
  const [options, setOptions] = useState({});
  const [productSaleOptions, setProductSaleOptions] = useState({});
  const [userRetentionOptions, setUserRetentionOptions] = useState({});
  const [newUsersOptions, setNewUsersOptions] = useState({});
  const [temperatureOptions, setTemperatureOptions] = useState({});
  const [activeTab, setActiveTab] = useState("products");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [endDate] = useState(dayjs().format("DD-MM-YYYY"));
  const [startDate] = useState(
    dayjs().subtract(2, "year").format("DD-MM-YYYY")
  );

  const navigate = useNavigate();
  const handleNavigate = (key) => {
    if (key === "transactions") navigate("/transactions");
  };
  const { auth } = useStore();
  const isAllAccess = auth?.user?.roles?.includes("all:access");
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    data: dashboardData,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    enabled: isAllAccess,
    queryKey: ["dashboard-data"],
    queryFn: () => services.getDashboardData().then((res) => res.data),
    onError: () => {
      toast.error(t("notifications.error.message"), {
        duration: 3000,
      });
    },
  });
  const {
    isLoading: customLoading,
    error: customError,
    data: customData,
  } = useQuery({
    queryKey: ["custom-data"],
    enabled:
      (activeTab === "spots" || activeTab === "spots-list") && isAllAccess,
    queryFn: async () => await services.getCustomDashboardData(),
    cacheTime: 1000,
    staleTime: 1000,
  });

  const { data: userRetentionReport, isLoading: isLoadingUserRetentionReport } =
    useQuery({
      queryKey: ["user-retention-report", startDate, endDate],
      enabled: isAllAccess,
      queryFn: () =>
        services
          .getUserRetentionReport(startDate, endDate)
          .then((res) => res.data),
      onError: () => {
        toast.error(t("notifications.error.message"), {
          duration: 3000,
        });
      },
    });

  const { data: newUsersReport, isLoading: isLoadingNewUsersReport } = useQuery(
    {
      queryKey: ["new-users-report"],
      enabled: isAllAccess,
      queryFn: () => services.getUserMonthlyReport().then((res) => res.data),
      onError: () => {
        toast.error(t("notifications.error.message"), {
          duration: 3000,
        });
      },
    }
  );

  const {
    data: productSaleReportDate,
    isLoading: isLoadingProductSaleReportDate,
  } = useQuery({
    queryKey: ["product-sale-report-date", startDate, endDate],
    enabled: isAllAccess,
    queryFn: () =>
      services
        .getProductSaleReportDate(startDate, endDate)
        .then((res) => res.data),
    onError: () => {
      toast.error(t("notifications.error.message"), {
        duration: 3000,
      });
    },
  });

  const { data: temperatureReport, isLoading: isLoadingTemperatureReport } =
    useQuery({
      queryKey: ["device-temp-report"],
      enabled: isAllAccess,
      queryFn: () => services.getDeviceTempReport().then((res) => res.data),
      onError: () => {
        toast.error(t("notifications.error.message"), {
          duration: 3000,
        });
      },
    });

  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [activeTab]);

  useCopilotReadable({
    description: "The current dashboard data",
    value: dashboardData,
  });
  useCopilotReadable({
    description: "The current new users report",
    value: newUsersReport,
  });

  useCopilotReadable({
    description: "The current product sale report",
    value: productSaleReportDate,
  });

  useCopilotReadable({
    description: "The current temperature report",
    value: temperatureReport,
  });

  const mobileDataZoom = [
    {
      type: "inside",
      start: 0,
      end: 100,
    },
    {
      type: "slider",
      start: 0,
      end: 20,
      bottom: 10,
    },
  ];

  useEffect(() => {
    if (dashboardData) {
      const formattedStats = [
        {
          id: 1,
          name: t("dashboard.registered-users"),
          value: dashboardData?.total_active_users,
        },
        {
          id: 2,
          name: t("dashboard.total-transaction-count"),
          value: dashboardData?.total_transaction_count,
        },
        {
          id: 3,
          name: t("dashboard.total-product-count"),
          value: dashboardData?.total_product_count,
        },
        {
          id: 4,
          name: t("dashboard.total-sales-amount"),
          key: "total_transaction_price",
          value: currencyFormatter(dashboardData.total_transaction_price),
        },
      ];
      setStats(formattedStats || []);
      const formattedDashboardData =
        dashboardData?.chart_data?.map((item) => ({
          name: dayjs(item.created_at).format("DD.MM.YYYY"),
          title: dayjs(item.created_at).format("DD.MM.YYYY HH:mm"),
          value: item.price,
        })) || [];
      setOptions({
        xAxis: {
          type: "category",
          data: formattedDashboardData.map((item) => item.name),
          axisLabel: {
            rotate: isMobile ? 45 : 30,
          },
        },
        yAxis: {
          type: "value",
          min: 0,
          max:
            Math.max(...formattedDashboardData.map((item) => item.value)) + 10,
          splitLine: {
            lineStyle: {
              color: "rgba(30,68,255,0.2)",
            },
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: function (params) {
            return (
              formattedDashboardData[params[0].dataIndex].title +
              "<br/>" +
              params[0].marker +
              " " +
              t("labels.amount-mny") +
              ": " +
              currencyFormatter(params[0].value)
            );
          },
        },
        grid: {
          left: "5%",
          right: "5%",
          bottom: isMobile ? "25%" : "15%",
          containLabel: true,
        },
        dataZoom: isMobile ? mobileDataZoom : [],
        series: [
          {
            data: formattedDashboardData.map((item) => item.value),
            type: "bar",
            showBackground: true,
            backgroundStyle: {
              color: "rgba(0,0,0,0.08)",
            },
            emphasis: {
              itemStyle: {
                borderRadius: [2, 2],
                color: "#1E44FF",
              },
            },
            itemStyle: {
              borderRadius: [2, 2],
              color: "#1E44FF",
            },
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData, isMobile, t]);

  useEffect(() => {
    if (userRetentionReport && userRetentionReport.length > 0) {
      const sortedData = [...userRetentionReport].sort(
        (a, b) => b.transaction_count - a.transaction_count
      );
      const topUsers = sortedData.slice(0, Math.min(sortedData.length, 50));
      setUserRetentionOptions({
        xAxis: {
          type: "category",
          data: topUsers.map((item) => {
            const label = item.label;
            return isMobile && label.length > 10
              ? label.substring(0, 7) + "..."
              : label.length > 15
              ? label.substring(0, 10) + "..."
              : label;
          }),
          axisLabel: {
            interval: 0,
            rotate: isMobile ? 45 : 30,
          },
        },
        yAxis: {
          type: "value",
          splitLine: {
            lineStyle: {
              color: "rgba(30,68,255,0.2)",
            },
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: function (params) {
            const item = topUsers[params[0].dataIndex];
            return (
              item.label +
              "<br/>" +
              params[0].marker +
              " " +
              t("labels.transaction-count") +
              ": " +
              item.transaction_count +
              "<br/>" +
              params[0].marker +
              " " +
              t("labels.amount-mny") +
              ": " +
              currencyFormatter(item.total_price)
            );
          },
        },
        grid: {
          left: "5%",
          right: "5%",
          bottom: isMobile ? "25%" : "15%",
          containLabel: true,
        },
        dataZoom: isMobile ? mobileDataZoom : [],
        series: [
          {
            data: topUsers.map((item) => item.transaction_count),
            name: t("labels.transaction-count"),
            type: "bar",
            showBackground: true,
            backgroundStyle: {
              color: "rgba(0,0,0,0.08)",
            },
            emphasis: {
              itemStyle: {
                borderRadius: [2, 2, 0, 0],
                color: "#1E44FF",
              },
            },
            itemStyle: {
              borderRadius: [2, 2, 0, 0],
              color: "#1E44FF",
            },
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRetentionReport, isMobile, t]);

  useEffect(() => {
    if (
      newUsersReport &&
      newUsersReport.monthly_new_user_count &&
      newUsersReport.monthly_new_user_count.length > 0
    ) {
      const series = [
        {
          data: newUsersReport.monthly_new_user_count.map((item) => item.count),
          name: t("labels.new-users-count"),
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 8,
          lineStyle: {
            width: 3,
            color: "#10B981",
          },
          itemStyle: {
            color: "#10B981",
            borderColor: "#ffffff",
            borderWidth: 2,
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: "rgba(16, 185, 129, 0.3)",
                },
                {
                  offset: 1,
                  color: "rgba(16, 185, 129, 0.05)",
                },
              ],
            },
          },
        },
      ];

      if (
        newUsersReport.monthly_new_user_count_with_cards &&
        newUsersReport.monthly_new_user_count_with_cards.length > 0
      ) {
        series.push({
          data: newUsersReport.monthly_new_user_count_with_cards.map(
            (item) => item.count
          ),
          name: t("labels.new-users-with-cards-count"),
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 8,
          lineStyle: {
            width: 3,
            color: "#F59E0B",
          },
          itemStyle: {
            color: "#F59E0B",
            borderColor: "#ffffff",
            borderWidth: 2,
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: "rgba(245, 158, 11, 0.3)",
                },
                {
                  offset: 1,
                  color: "rgba(245, 158, 11, 0.05)",
                },
              ],
            },
          },
        });
      }

      // Add third series if transaction_success_user_count exists
      if (
        newUsersReport.transaction_success_user_count &&
        newUsersReport.transaction_success_user_count.length > 0
      ) {
        series.push({
          data: newUsersReport.transaction_success_user_count.map(
            (item) => item.count
          ),
          name: t("labels.transaction-success-user-count"),
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 8,
          lineStyle: {
            width: 3,
            color: "#3B82F6",
          },
          itemStyle: {
            color: "#3B82F6",
            borderColor: "#ffffff",
            borderWidth: 2,
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: "rgba(59, 130, 246, 0.3)",
                },
                {
                  offset: 1,
                  color: "rgba(59, 130, 246, 0.05)",
                },
              ],
            },
          },
        });
      }

      setNewUsersOptions({
        xAxis: {
          type: "category",
          data: newUsersReport.monthly_new_user_count.map((item) => item.month),
          axisLabel: {
            interval: 0,
            rotate: isMobile ? 45 : 30,
          },
        },
        yAxis: {
          type: "value",
          min: 0,
          splitLine: {
            lineStyle: {
              color: "rgba(30,68,255,0.2)",
            },
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: function (params) {
            const monthData =
              newUsersReport.monthly_new_user_count[params[0].dataIndex];
            let tooltipContent = monthData.month + "<br/>";

            params.forEach((param) => {
              if (param.seriesName === t("labels.new-users-count")) {
                tooltipContent +=
                  param.marker +
                  " " +
                  t("labels.new-users-count") +
                  ": " +
                  param.value +
                  "<br/>";
              } else if (
                param.seriesName === t("labels.new-users-with-cards-count")
              ) {
                tooltipContent +=
                  param.marker +
                  " " +
                  t("labels.new-users-with-cards-count") +
                  ": " +
                  param.value +
                  "<br/>";
              } else if (
                param.seriesName === t("labels.transaction-success-user-count")
              ) {
                tooltipContent +=
                  param.marker +
                  " " +
                  t("labels.transaction-success-user-count") +
                  ": " +
                  param.value +
                  "<br/>";
              }
            });

            return tooltipContent;
          },
        },
        legend: {
          show: true,
          bottom: 0,
          left: "center",
        },
        grid: {
          left: "5%",
          right: "5%",
          bottom: isMobile ? "35%" : "25%",
          containLabel: true,
        },
        dataZoom: isMobile ? mobileDataZoom : [],
        series: series,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newUsersReport, isMobile, t]);

  useEffect(() => {
    if (productSaleReportDate && productSaleReportDate.length > 0) {
      setProductSaleOptions({
        xAxis: {
          type: "category",
          data: productSaleReportDate.map((item) => item.label),
          axisLabel: {
            interval: 0,
            rotate: isMobile ? 45 : 30,
          },
        },
        yAxis: {
          type: "value",
          min: 0,
          max:
            Math.max(
              ...productSaleReportDate.map((item) => item.transaction_count)
            ) + 100,
          splitLine: {
            lineStyle: {
              color: "rgba(30,68,255,0.2)",
            },
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: function (params) {
            const item = productSaleReportDate[params[0].dataIndex];
            return (
              item.label +
              "<br/>" +
              params[0].marker +
              " " +
              t("labels.transaction-count") +
              ": " +
              item.transaction_count +
              "<br/>" +
              params[0].marker +
              " " +
              t("labels.product-count") +
              ": " +
              item.product_count +
              "<br/>" +
              params[0].marker +
              " " +
              t("labels.customer-count") +
              ": " +
              item.total_customer_count +
              "<br/>" +
              params[0].marker +
              " " +
              t("labels.amount-mny") +
              ": " +
              currencyFormatter(item.total_price) +
              "<br/>"
            );
          },
        },
        grid: {
          left: "5%",
          right: "5%",
          bottom: isMobile ? "25%" : "15%",
          containLabel: true,
        },
        dataZoom: isMobile ? mobileDataZoom : [],
        series: [
          {
            data: productSaleReportDate.map((item) => item.transaction_count),
            name: t("labels.transaction-count"),
            type: "bar",
            showBackground: true,
            backgroundStyle: {
              color: "rgba(0,0,0,0.08)",
            },
            emphasis: {
              itemStyle: {
                borderRadius: [2, 2],
                color: "#1E44FF",
              },
            },
            itemStyle: {
              borderRadius: [2, 2],
              color: "#1E44FF",
            },
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSaleReportDate, isMobile, t]);

  useEffect(() => {
    if (temperatureReport) {
      // Backend'den gelen veriyi direkt kullan
      setTemperatureOptions(temperatureReport);
    }
  }, [temperatureReport]);

  const handleTabChange = (value) => {
    setActiveTab(value);
  };

  const renderChart = () => {
    switch (activeTab) {
      case "products":
        return (
          <div className="h-full w-full">
            {isLoadingProductSaleReportDate && (
              <div className="absolute top-0 left-0 w-full h-full bg-gray-400/10">
                <BarLoading />
              </div>
            )}
            {!isLoadingProductSaleReportDate && productSaleReportDate && (
              <ReactECharts
                className="md:!h-full md:w-[98%] mx-auto"
                option={productSaleOptions}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: "100%", width: "100%" }}
              />
            )}
          </div>
        );

      case "spots":
        return (
          <div className="h-full w-full">
            {customLoading && (
              <div className="absolute top-0 left-0 w-full h-full bg-gray-400/10">
                <BarLoading />
              </div>
            )}
            {!customLoading && customData && (
              <MapComponent data={customData?.data} />
            )}
          </div>
        );
      case "spots-list":
        return (
          <div className="h-full w-full">
            {customLoading && (
              <div className="absolute top-0 left-0 w-full h-full bg-gray-400/10">
                <BarLoading />
              </div>
            )}
            {!customLoading && customData && (
              <div className="h-screen w-full pb-8">
                <SpotsCustomList data={customData?.data} />
              </div>
            )}
          </div>
        );
      case "users":
        return (
          <div className="h-full w-full">
            {isLoadingUserRetentionReport && (
              <div className="absolute top-0 left-0 w-full h-full bg-gray-400/10">
                <BarLoading />
              </div>
            )}
            {!isLoadingUserRetentionReport && userRetentionReport && (
              <ReactECharts
                className="md:!h-full md:w-[98%] mx-auto"
                option={userRetentionOptions}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: "100%", width: "100%" }}
              />
            )}
          </div>
        );
      case "new-users":
        return (
          <div className="h-full w-full">
            {isLoadingNewUsersReport && (
              <div className="absolute top-0 left-0 w-full h-full bg-gray-400/10">
                <BarLoading />
              </div>
            )}
            {!isLoadingNewUsersReport &&
              newUsersReport &&
              newUsersReport.monthly_new_user_count &&
              newUsersReport.monthly_new_user_count.length > 0 &&
              Object.keys(newUsersOptions).length > 0 && (
                <ReactECharts
                  className="md:!h-full md:w-[98%] mx-auto"
                  option={newUsersOptions}
                  notMerge={true}
                  lazyUpdate={true}
                  style={{ height: "100%", width: "100%" }}
                />
              )}
          </div>
        );
      case "temperature":
        return (
          <div className="h-full w-full">
            {isLoadingTemperatureReport && (
              <div className="absolute top-0 left-0 w-full h-full bg-gray-400/10">
                <BarLoading />
              </div>
            )}
            {!isLoadingTemperatureReport &&
              temperatureReport &&
              Object.keys(temperatureOptions).length > 0 && (
                <ReactECharts
                  className="md:!h-full md:pb-14 lg:pb-4 md:w-[98%] mx-auto"
                  option={temperatureOptions}
                  notMerge={true}
                  lazyUpdate={true}
                  style={{ height: "100%", width: "100%" }}
                />
              )}
          </div>
        );
      default:
        return null;
    }
  };

  const dashboardTabs = [
    { value: "products", label: t("dashboard.product-sales-chart") },
    { value: "spots", label: t("dashboard.spots") },
    { value: "spots-list", label: t("dashboard.spots-list") },
    { value: "users", label: t("dashboard.user-retention-chart") },
    { value: "new-users", label: t("dashboard.new-users-chart") },
    { value: "temperature", label: t("dashboard.temperature-chart") },
  ];

  return (
    <>
      {isAllAccess && (
        <div className="flex flex-col">
          {isLoading ? (
            <div className="bg-gray-400/10 h-full w-full">
              <BarLoading />
            </div>
          ) : (
            <div className="flex flex-col h-full justify-between">
              <div>
                <DashboardStats data={stats} />
              </div>
              <div className="flex gap-4 flex-col lg:flex-nowrap place-items-center h-full pb-4">
                <div className="w-full flex flex-col md:flex-row   gap-2 md:justify-between items-center px-8 mt-4">
                  <CustomTabs
                    tabs={dashboardTabs}
                    activeTab={activeTab}
                    onChange={handleTabChange}
                  />

                  <Button
                    onClick={() => handleNavigate("transactions")}
                    color="#1E44FF"
                    variant="transparent"
                  >
                    {t("actions.go-to-transactions")}
                  </Button>
                </div>

                <div
                  className={`py-8 rounded-2xl w-full h-[60dvh] overflow-scroll relative block ${
                    activeTab === "spots" ? "bg-transparent" : "bg-gray-400/10"
                  }`}
                >
                  {renderChart()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Home;
